import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DeliveryContext } from "../contexts/DeliveryContext";
import { CartContext } from "../contexts/CartContext";
import { MessagesContext } from "../contexts/MessagesContext";
import useCreateOrder from "../services/useCreateOrder";
import useSeed from "../services/useSeed";
import { IDeliveryContext } from "../types/delivery";
import { ICartContext } from "../types/cart";
import { IMessagesContext } from "../types/messages";
import { getWeekdayName } from "../utils/datetimeUtils";
import formatPhone from "../utils/formatPhone";
import { ReactNode } from "react";
import { IDeliveryMethod } from "../types/seed";
import usePerfilPU from "../services/usePerfilPU";
import { IAddress } from "../types/seed";
import { getBestAddress } from "../utils/addressUtils";
import { IOrder } from "../types/order";
import useFair from "../services/useFair";

/**
 * Extrai o erro de endereço fora da área de entrega de um erro da API
 */
const extractOutsideDeliveryAreaError = (
  error: any,
): { code: string; message: string } | null => {
  const errorData = error?.response?.data ?? error?.data ?? error;
  const addressErrors = Array.isArray(errorData?.address)
    ? errorData.address
    : [];
  const outsideAreaError = addressErrors.find(
    (err: any) => err && err.code === "outside_delivery_area",
  );
  return outsideAreaError || null;
};

const useCheckout = () => {
  const { isLoading: isLoadingUrbanPartner, data: urbanPartner } =
    usePerfilPU();
  const { address, deliveryMethodId, setDeliveryMethodId, setAddress } =
    useContext(DeliveryContext) as IDeliveryContext;
  const { emptyCart, getCart, removeItem, syncCartFair } = useContext(
    CartContext,
  ) as ICartContext;
  const { addMessage } = useContext(MessagesContext) as IMessagesContext;
  const { slug } = useParams();
  const cart = getCart(slug);
  const {
    error,
    isError,
    isLoading: isLoadingCreateOrder,
    mutateAsync: createOrderAsync,
    reset,
  } = useCreateOrder();
  const [open, setOpen] = useState(false);
  const [newAddress, setNewAddress] = useState("");
  const [isFormValid, setIsFormValid] = useState(true);
  const navigate = useNavigate();
  const { data: seed, isLoading: isLoadingSeed } = useSeed();
  const { data: fair, isLoading: isLoadingFair } = useFair();
  const [activeStep, setActiveStep] = useState(0);
  const [addressObj, setAddressObj] = useState<IAddress | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [paymentOrder, setPaymentOrder] = useState<{
    id: number;
    total: number;
  } | null>(null);

  const items = Object.keys(cart).map((productId) => cart[parseInt(productId)]);
  const subtotal = items.reduce((total, item) => {
    return total + item.amount * parseFloat(item.product.price);
  }, 0);
  const credit =
    urbanPartner && seed && urbanPartner.credit[seed.slug]
      ? parseFloat(urbanPartner.credit[seed.slug].total_credit)
      : 0;
  const totalItems = items.length;
  const deliveryWeekday = seed ? getWeekdayName(seed.delivery_weekday) : "";
  const deliveryMethod = seed
    ? seed.delivery_methods.find(
        (deliveryMethod: IDeliveryMethod) =>
          deliveryMethod.id === deliveryMethodId,
      )
    : null;

  useEffect(() => {
    const totalItems = Object.keys(cart).length;
    if (totalItems === 0 && activeStep !== 3) {
      navigate(`/${slug}`);
    }
  }, [cart, navigate, slug, activeStep]);

  useEffect(() => {
    if (!slug || !fair) {
      return;
    }

    const cartInvalidated = syncCartFair(slug, fair.id);
    if (cartInvalidated) {
      addMessage({
        closeable: true,
        content:
          "A feira mudou e seu carrinho foi esvaziado. Monte o pedido novamente para continuar.",
        severity: "warning",
      });
      setActiveStep(0);
    }
  }, [addMessage, fair, slug, syncCartFair]);

  useEffect(() => {
    if (isError) {
      const anyError = error as any;
      const outsideAreaError = extractOutsideDeliveryAreaError(anyError);

      // Se for erro de área de entrega, não mostra mensagem genérica (já está sendo tratado)
      if (outsideAreaError) {
        return;
      }

      let errorMessage: ReactNode = `Houve um problema ao criar seu pedido, entre em contato com a sua semeadora em ${formatPhone(seed?.seeder.phone || "")}.`;
      if (
        anyError &&
        anyError.code &&
        anyError.code[0] === "out_of_stock_products"
      ) {
        const productNames = anyError.details.products
          .map((product: { name: string }) => product.name)
          .join(", ");
        errorMessage = `Os seguintes produtos do seu carrinho estão fora de estoque: ${productNames}. Se quiser removê-los, atualize seu carrinho.`;
      }
      addMessage({
        closeable: true,
        content: errorMessage,
        severity: "error",
      });
    }
  }, [addMessage, error, isError, removeItem, reset, seed?.seeder.phone, slug]);

  useEffect(() => {
    if (seed && seed.delivery_methods.length > 0) {
      setDeliveryMethodId(seed.delivery_methods[0].id);
    }
  }, [seed, setDeliveryMethodId]);

  // Inicializa addressObj a partir do urbanPartner quando disponível
  useEffect(() => {
    if (addressObj === null && urbanPartner) {
      const defaultAddressObj = urbanPartner.address_obj ?? null;
      setAddressObj(defaultAddressObj);
    }
  }, [urbanPartner, addressObj]);

  // Sempre deriva address (string) de addressObj quando addressObj existir
  useEffect(() => {
    if (addressObj) {
      const addressText = getBestAddress(addressObj, "");
      setAddress(addressText);
    }
  }, [addressObj, setAddress]);

  const total = () => {
    if (deliveryMethod) {
      const fee = parseFloat(deliveryMethod.fee) || 0;
      if (credit > subtotal + fee) {
        return 0;
      } else {
        const total = subtotal - credit + fee;
        return total;
      }
    } else {
      if (credit > subtotal) {
        return 0;
      } else {
        const total = subtotal - credit;
        return total;
      }
    }
  };

  const handleCreateOrder = async () => {
    if (address !== null) {
      if (slug && fair) {
        const cartInvalidated = syncCartFair(slug, fair.id);
        if (cartInvalidated) {
          addMessage({
            closeable: true,
            content:
              "A feira mudou enquanto você finalizava a compra. Seu carrinho foi esvaziado.",
            severity: "warning",
          });
          setActiveStep(0);
          return;
        }
      }

      // Limpar erro anterior antes de tentar novamente
      setAddressError(null);

      const payload: IOrder = {
        address: address ? address : "",
        items: Object.keys(cart).map((productId) => {
          return {
            product: parseInt(productId),
            amount: cart[parseInt(productId)].amount,
          };
        }),
        delivery_method: deliveryMethodId ? deliveryMethodId : null,
        slug: slug ? slug : "",
      };

      // Adicionar address_obj se disponível
      if (addressObj) {
        payload.address_obj = addressObj;
      }

      try {
        const order = await createOrderAsync(payload);
        if (!order || typeof order.id !== "number") {
          throw new Error("Pedido criado sem identificação.");
        }
        // Só avança se a criação foi bem-sucedida
        setActiveStep((prev) => prev + 1);
        setPaymentOrder({ id: order.id, total: Number(order.total) });
        emptyCart();
        setAddress(null);
        setAddressObj(null);
      } catch (error: any) {
        // Tentar extrair erro específico de endereço fora da área
        const outsideAreaError = extractOutsideDeliveryAreaError(error);

        if (outsideAreaError) {
          // Erro específico de área de entrega
          setAddressError(
            outsideAreaError.message ||
              "Endereço fora da área de entrega da feira.",
          );
          // Voltar para o passo de entrega para que o usuário veja o erro e possa corrigir
          setActiveStep(1);
        }
        // Outros erros são tratados pelo useEffect existente que monitora isError
      }
    }
  };

  const handleSave = (address_obj?: IAddress | null) => {
    if (address_obj) {
      setAddressObj(address_obj);
      setAddress(getBestAddress(address_obj, ""));
      // Limpar erro de endereço quando o usuário alterar o endereço
      setAddressError(null);
    }
    setOpen(false);
  };

  const handleNext = () => {
    if (activeStep === 0 && !urbanPartner) {
      return;
    }
    if (activeStep === 1 && !deliveryMethod) {
      return;
    }
    if (activeStep === 2) {
      handleCreateOrder();
      return;
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  return {
    isLoadingUrbanPartner,
    isLoadingSeed,
    isLoadingFair,
    isLoadingCreateOrder,
    urbanPartner,
    address,
    setAddress,
    deliveryMethod,
    deliveryMethodId,
    setDeliveryMethodId,
    emptyCart,
    getCart,
    removeItem,
    addMessage,
    slug,
    cart,
    open,
    setOpen,
    newAddress,
    setNewAddress,
    isFormValid,
    setIsFormValid,
    navigate,
    seed,
    fair,
    activeStep,
    setActiveStep,
    items,
    subtotal,
    credit,
    totalItems,
    deliveryWeekday,
    total,
    handleCreateOrder,
    handleSave,
    handleNext,
    handleBack,
    addressError,
    paymentOrder,
  };
};

export default useCheckout;
